import React from 'react';

import { useIntl } from 'react-intl';

import Lozenge from '@atlaskit/lozenge/lozenge';
import type { SemanticColor } from '@atlaskit/lozenge/types';

import { graphWorkItemStatusMessages } from './messages';

/**
 * Map a third-party **work-item** status (e.g. GitHub issue states) onto the
 * appropriate Atlaskit Lozenge appearance.
 *
 * Only the two canonical GitHub work-item states (`OPEN`, `CLOSED`) are
 * supported. Inputs are case-insensitive — callers may pass the value as it
 * comes from the API (`"open"`, `"OPEN"`, …). Anything else falls back to
 * `'neutral'`.
 *
 * Kept separate from `getPullRequestStatusAppearance` so the two domains stay
 * decoupled and additions to either map can't accidentally affect the other.
 */
const workItemStatusToLozengeMap: Record<string, SemanticColor> = {
	OPEN: 'information', // blue
	CLOSED: 'success', // green
};

const getAppearance = (status?: string): SemanticColor =>
	(status ? workItemStatusToLozengeMap[status.toUpperCase()] : undefined) ?? 'neutral';

type GraphWorkItemStatusLozengeProps = {
	status?: string;
};

/**
 * Renders a status lozenge for a **third-party Graph work item** (e.g. GitHub),
 * translating the canonical raw enum values (`OPEN`, `CLOSED`) into the
 * viewer's locale via `react-intl`. Unknown statuses fall through to the raw
 * string verbatim with a neutral appearance.
 *
 * **Not for Jira issues.** Jira statuses are tenant-configurable and already
 * localised server-side; rendering them through this component would not
 * translate custom statuses and could double-translate stock ones. Use
 * `getStatus` from `../jira-status` for Jira issue rows instead.
 *
 * Appearance is derived from the **raw** (pre-translation) `status` so that
 * the lozenge colour stays correct in every locale — passing the translated
 * string through `getAppearance` would fail its lookup (e.g. "Cerrado" would
 * not match "closed") and fall back to neutral.
 */
export const GraphWorkItemStatusLozenge = ({
	status,
}: GraphWorkItemStatusLozengeProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!status) {
		return null;
	}

	const message =
		graphWorkItemStatusMessages[status.toLowerCase() as keyof typeof graphWorkItemStatusMessages];
	const label = message ? formatMessage(message) : status;

	return (
		<Lozenge appearance={getAppearance(status)} testId="graph-work-item-status-lozenge-test-id">
			{label}
		</Lozenge>
	);
};
